import React, { useEffect, useState } from "react";
import { Shell } from "../components/Shell";
import { Tabs } from "../components/Tabs";
import { api, ApiError } from "../api";
import { useAuth } from "../auth/AuthContext";

type Cliente = {
  id: number; nombre: string; nit: string | null; telefono: string | null; email: string | null;
  listaPrecioNombre: string | null; activo: boolean; tipoPersona: string; regimenTributario: string;
  agenteRetenedor: boolean; granContribuyente: boolean; actividadEconomicaCiiu: string | null; municipioIca: string | null;
};
type ListaPrecio = { id: number; nombre: string; activo: boolean; precios: { id: number; producto: string; precio: number }[] };
type Pedido = { id: number; clienteNombre: string; numero: string; fecha: string; estado: string; totalEstimado: number; items: number };
type Factura = {
  id: number; pedidoNumero: string; clienteNombre: string; numero: string; estadoDian: string;
  cufeDian?: string | null; fecha: string; valorTotal: number;
};
type CarteraItem = { id: number; facturaNumero: string; clienteNombre: string; saldo: number; fechaVencimiento: string; estado: string };
type InvPT = { id: number; producto: string; cantidad: number; bodegaNombre: string };
type CotizacionItem = { id: number; producto: string; cantidad: number; precio: number };
type Cotizacion = {
  id: number; clienteId: number; clienteNombre: string; numero: string; fecha: string; fechaVencimiento: string | null;
  estado: string; observaciones: string | null; pedidoId: number | null; detalle: CotizacionItem[];
};
type Seguimiento = {
  id: number; clienteId: number; clienteNombre: string; tipo: string; fecha: string; descripcion: string;
  proximoSeguimiento: string | null; usuarioNombre: string | null;
};

const ESTADO_PEDIDO_BADGE: Record<string, string> = {
  borrador: "bg-slate-100 text-slate-500", confirmado: "bg-amber-50 text-amber-700",
  facturado: "bg-accent-light text-accent-dark", cancelado: "bg-red-50 text-red-700",
};
const ESTADO_DIAN_BADGE: Record<string, string> = {
  simulado: "bg-slate-100 text-slate-500", pendiente: "bg-amber-50 text-amber-700",
  timbrada: "bg-accent-light text-accent-dark", rechazada: "bg-red-50 text-red-700",
};
const ESTADO_COTIZACION_BADGE: Record<string, string> = {
  borrador: "bg-slate-100 text-slate-500", enviada: "bg-amber-50 text-amber-700",
  aceptada: "bg-accent-light text-accent-dark", rechazada: "bg-red-50 text-red-700",
  convertida: "bg-indigo-50 text-indigo-700",
};
const TIPO_SEGUIMIENTO_LABEL: Record<string, string> = {
  llamada: "Llamada", visita: "Visita", email: "Email", whatsapp: "WhatsApp", nota: "Nota",
};

export function Comercial() {
  const { tienePermiso } = useAuth();
  const puedeCrear = tienePermiso("comercial:crear");
  const puedeEditar = tienePermiso("comercial:editar");
  const puedeFacturar = tienePermiso("comercial:facturar");
  const puedeTimbrar = tienePermiso("dian:timbrar");

  const [tab, setTab] = useState("clientes");
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [listas, setListas] = useState<ListaPrecio[]>([]);
  const [pedidos, setPedidos] = useState<Pedido[]>([]);
  const [facturas, setFacturas] = useState<Factura[]>([]);
  const [cartera, setCartera] = useState<CarteraItem[]>([]);
  const [inventarioPt, setInventarioPt] = useState<InvPT[]>([]);
  const [cotizaciones, setCotizaciones] = useState<Cotizacion[]>([]);
  const [seguimientos, setSeguimientos] = useState<Seguimiento[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cargar = async () => {
    setCargando(true);
    setError(null);
    try {
      const [c, l, p, f, cx, inv, cot, seg] = await Promise.all([
        api.get("/comercial/clientes"),
        api.get("/comercial/listas-precios"),
        api.get("/comercial/pedidos"),
        api.get("/comercial/facturas"),
        api.get("/comercial/cartera"),
        api.get("/inventarios/producto-terminado"),
        api.get("/comercial/cotizaciones"),
        api.get("/comercial/seguimientos"),
      ]);
      setClientes(c); setListas(l); setPedidos(p); setFacturas(f); setCartera(cx); setInventarioPt(inv);
      setCotizaciones(cot); setSeguimientos(seg);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo cargar comercial.");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  return (
    <Shell titulo="Comercial" breadcrumb="Comercial">
      <p className="text-sm text-textmuted mb-4">
        Clientes, listas de precios, cotizaciones, pedidos y su facturación. Facturar descuenta el inventario de producto terminado y abre la cartera por cobrar.
        Las cotizaciones se convierten en pedido cuando el cliente confirma, y el seguimiento comercial deja registro de cada gestión con el cliente.
      </p>
      <Tabs
        tabs={[
          { key: "clientes", label: "Clientes" },
          { key: "precios", label: "Listas de precios" },
          { key: "cotizaciones", label: "Cotizaciones" },
          { key: "pedidos", label: "Pedidos" },
          { key: "facturas", label: "Facturas" },
          { key: "cartera", label: "Cartera" },
          { key: "seguimientos", label: "Seguimiento comercial" },
        ]}
        activo={tab} onChange={setTab}
      />
      {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-4">{error}</div>}
      {cargando ? (
        <p className="text-sm text-textmuted">Cargando...</p>
      ) : (
        <>
          {tab === "clientes" && <TabClientes clientes={clientes} listas={listas} puedeCrear={puedeCrear} puedeEditar={puedeEditar} recargar={cargar} />}
          {tab === "precios" && <TabPrecios listas={listas} puedeCrear={puedeCrear} puedeEditar={puedeEditar} recargar={cargar} />}
          {tab === "cotizaciones" && (
            <TabCotizaciones cotizaciones={cotizaciones} clientes={clientes} inventarioPt={inventarioPt}
              puedeCrear={puedeCrear} puedeEditar={puedeEditar} recargar={cargar} />
          )}
          {tab === "pedidos" && (
            <TabPedidos pedidos={pedidos} clientes={clientes} inventarioPt={inventarioPt}
              puedeCrear={puedeCrear} puedeFacturar={puedeFacturar} recargar={cargar} />
          )}
          {tab === "facturas" && (
            <TabFacturas facturas={facturas} clientes={clientes} inventarioPt={inventarioPt}
              puedeCrear={puedeCrear} puedeFacturar={puedeFacturar} puedeTimbrar={puedeTimbrar} recargar={cargar} />
          )}
          {tab === "cartera" && <TabCartera cartera={cartera} puedeFacturar={puedeFacturar} recargar={cargar} />}
          {tab === "seguimientos" && (
            <TabSeguimientos seguimientos={seguimientos} clientes={clientes} puedeCrear={puedeCrear} recargar={cargar} />
          )}
        </>
      )}
    </Shell>
  );
}

function TabClientes({ clientes, listas, puedeCrear, puedeEditar, recargar }: {
  clientes: Cliente[]; listas: ListaPrecio[]; puedeCrear: boolean; puedeEditar: boolean; recargar: () => void;
}) {
  const [modalAbierto, setModalAbierto] = useState(false);
  const [nombre, setNombre] = useState("");
  const [nit, setNit] = useState("");
  const [telefono, setTelefono] = useState("");
  const [email, setEmail] = useState("");
  const [listaPrecioId, setListaPrecioId] = useState("");
  const [regimenTributario, setRegimenTributario] = useState("ordinario");
  const [agenteRetenedor, setAgenteRetenedor] = useState(false);
  const [granContribuyente, setGranContribuyente] = useState(false);
  const [municipioIca, setMunicipioIca] = useState("");
  const [actividadCiiu, setActividadCiiu] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.post("/comercial/clientes", {
        nombre, nit, telefono, email, listaPrecioId: listaPrecioId ? Number(listaPrecioId) : null,
        regimenTributario, agenteRetenedor, granContribuyente,
        municipioIca: municipioIca || null, actividadEconomicaCiiu: actividadCiiu || null,
      });
      setModalAbierto(false);
      setNombre(""); setNit(""); setTelefono(""); setEmail(""); setListaPrecioId("");
      setRegimenTributario("ordinario"); setAgenteRetenedor(false); setGranContribuyente(false);
      setMunicipioIca(""); setActividadCiiu("");
      recargar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo crear el cliente.");
    } finally {
      setEnviando(false);
    }
  };

  const desactivar = async (c: Cliente) => {
    try { await api.put(`/comercial/clientes/${c.id}`, { activo: !c.activo }); recargar(); } catch { /* noop */ }
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        {puedeCrear && <button className="btn-primary" onClick={() => setModalAbierto(true)}>+ Nuevo cliente</button>}
      </div>
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-borderc"><th className="table-th">Nombre</th><th className="table-th">NIT</th><th className="table-th">Lista de precios</th><th className="table-th">Impuestos</th><th className="table-th">Estado</th><th className="table-th"></th></tr></thead>
          <tbody>
            {clientes.map((c) => (
              <tr key={c.id} className="border-b border-borderc last:border-0">
                <td className="table-td font-medium">{c.nombre}</td>
                <td className="table-td">{c.nit}</td>
                <td className="table-td">{c.listaPrecioNombre || "—"}</td>
                <td className="table-td">
                  <div className="flex flex-wrap gap-1">
                    {c.granContribuyente && <span className="badge bg-amber-50 text-amber-700 text-[10px]">Gran contrib.</span>}
                    {c.agenteRetenedor && !c.granContribuyente && <span className="badge bg-amber-50 text-amber-700 text-[10px]">Agente retenedor</span>}
                    {!c.agenteRetenedor && !c.granContribuyente && <span className="badge bg-slate-100 text-slate-500 text-[10px]">No retiene</span>}
                  </div>
                </td>
                <td className="table-td"><span className={`badge ${c.activo ? "bg-accent-light text-accent-dark" : "bg-slate-100 text-slate-500"}`}>{c.activo ? "activo" : "inactivo"}</span></td>
                <td className="table-td text-right">{puedeEditar && <button className="text-accent-dark hover:underline text-xs font-semibold" onClick={() => desactivar(c)}>{c.activo ? "Desactivar" : "Activar"}</button>}</td>
              </tr>
            ))}
            {clientes.length === 0 && <tr><td colSpan={6} className="table-td text-center text-textmuted py-8">Sin clientes registrados.</td></tr>}
          </tbody>
        </table>
      </div>
      {modalAbierto && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
            <h3 className="text-base font-semibold text-slate-800">Nuevo cliente</h3>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Nombre</label><input className="input-field" value={nombre} onChange={(e) => setNombre(e.target.value)} required /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">NIT</label><input className="input-field" value={nit} onChange={(e) => setNit(e.target.value)} /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Teléfono</label><input className="input-field" value={telefono} onChange={(e) => setTelefono(e.target.value)} /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Email</label><input className="input-field" type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Lista de precios</label>
              <select className="input-field" value={listaPrecioId} onChange={(e) => setListaPrecioId(e.target.value)}>
                <option value="">Sin asignar</option>
                {listas.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
              </select></div>

            <div className="border-t border-borderc pt-3">
              <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">Atributos tributarios</p>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-semibold text-slate-600 mb-1">Régimen tributario</label>
                  <select className="input-field" value={regimenTributario} onChange={(e) => setRegimenTributario(e.target.value)}>
                    <option value="ordinario">Ordinario</option>
                    <option value="simple">Régimen simple</option>
                  </select></div>
                <div><label className="block text-xs font-semibold text-slate-600 mb-1">Municipio ICA</label>
                  <input className="input-field" value={municipioIca} onChange={(e) => setMunicipioIca(e.target.value)} placeholder="Ej. Cali" /></div>
                <div className="col-span-2"><label className="block text-xs font-semibold text-slate-600 mb-1">Actividad CIIU</label>
                  <input className="input-field" value={actividadCiiu} onChange={(e) => setActividadCiiu(e.target.value)} placeholder="Ej. 4711" /></div>
              </div>
              <div className="flex gap-4 mt-3">
                <label className="flex items-center gap-2 text-xs text-slate-600">
                  <input type="checkbox" checked={agenteRetenedor} onChange={(e) => setAgenteRetenedor(e.target.checked)} /> Agente retenedor
                </label>
                <label className="flex items-center gap-2 text-xs text-slate-600">
                  <input type="checkbox" checked={granContribuyente} onChange={(e) => setGranContribuyente(e.target.checked)} /> Gran contribuyente
                </label>
              </div>
              <p className="text-[11px] text-textmuted mt-2">
                Si este cliente retiene impuestos al pagar (agente retenedor / gran contribuyente), el sistema calcula
                automáticamente la retefuente y el ReteICA al facturarle — ver módulo de Contabilidad.
              </p>
            </div>

            {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-ghost" onClick={() => setModalAbierto(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Guardando..." : "Crear"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function TabPrecios({ listas, puedeCrear, puedeEditar, recargar }: { listas: ListaPrecio[]; puedeCrear: boolean; puedeEditar: boolean; recargar: () => void }) {
  const [nombreLista, setNombreLista] = useState("");
  const [creandoLista, setCreandoLista] = useState(false);
  const [precioForm, setPrecioForm] = useState<Record<number, { producto: string; precio: string }>>({});

  const crearLista = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreandoLista(true);
    try {
      await api.post("/comercial/listas-precios", { nombre: nombreLista });
      setNombreLista("");
      recargar();
    } catch { /* noop */ } finally { setCreandoLista(false); }
  };

  const agregarPrecio = async (listaId: number, e: React.FormEvent) => {
    e.preventDefault();
    const f = precioForm[listaId];
    if (!f?.producto || !f?.precio) return;
    try {
      await api.post(`/comercial/listas-precios/${listaId}/precios`, { producto: f.producto, precio: Number(f.precio) });
      setPrecioForm((prev) => ({ ...prev, [listaId]: { producto: "", precio: "" } }));
      recargar();
    } catch { /* noop */ }
  };

  return (
    <div className="space-y-5">
      {puedeCrear && (
        <form onSubmit={crearLista} className="card p-4 flex items-end gap-2">
          <div className="flex-1">
            <label className="block text-xs font-semibold text-slate-600 mb-1">Nueva lista de precios</label>
            <input className="input-field" value={nombreLista} onChange={(e) => setNombreLista(e.target.value)} placeholder="ej. Lista Mayoristas 2026" required />
          </div>
          <button className="btn-primary" disabled={creandoLista}>+ Crear lista</button>
        </form>
      )}
      {listas.map((l) => (
        <div key={l.id} className="card p-5">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">{l.nombre}</h3>
          <table className="w-full mb-3">
            <thead><tr className="border-b border-borderc"><th className="table-th">Producto</th><th className="table-th">Precio</th></tr></thead>
            <tbody>
              {l.precios.map((p) => (
                <tr key={p.id} className="border-b border-borderc last:border-0">
                  <td className="table-td">{p.producto}</td>
                  <td className="table-td font-semibold">${p.precio.toLocaleString()}</td>
                </tr>
              ))}
              {l.precios.length === 0 && <tr><td colSpan={2} className="table-td text-center text-textmuted py-4">Sin precios definidos.</td></tr>}
            </tbody>
          </table>
          {puedeEditar && (
            <form onSubmit={(e) => agregarPrecio(l.id, e)} className="flex gap-2 border-t border-borderc pt-3">
              <input className="input-field" placeholder="Producto" value={precioForm[l.id]?.producto || ""}
                onChange={(e) => setPrecioForm((prev) => ({ ...prev, [l.id]: { producto: e.target.value, precio: prev[l.id]?.precio || "" } }))} />
              <input className="input-field w-32" type="number" step="0.01" placeholder="Precio" value={precioForm[l.id]?.precio || ""}
                onChange={(e) => setPrecioForm((prev) => ({ ...prev, [l.id]: { producto: prev[l.id]?.producto || "", precio: e.target.value } }))} />
              <button className="btn-secondary text-xs">+ Agregar</button>
            </form>
          )}
        </div>
      ))}
    </div>
  );
}

function TabPedidos({ pedidos, clientes, inventarioPt, puedeCrear, puedeFacturar, recargar }: {
  pedidos: Pedido[]; clientes: Cliente[]; inventarioPt: InvPT[]; puedeCrear: boolean; puedeFacturar: boolean; recargar: () => void;
}) {
  const [modalAbierto, setModalAbierto] = useState(false);
  const [clienteId, setClienteId] = useState("");
  const [numero, setNumero] = useState("");
  const [fecha, setFecha] = useState("");
  const [items, setItems] = useState<{ inventarioPtId: string; cantidad: string; precio: string }[]>([{ inventarioPtId: "", cantidad: "", precio: "" }]);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const actualizarItem = (idx: number, campo: string, valor: string) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [campo]: valor } : it)));
  };

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.post("/comercial/pedidos", {
        clienteId: Number(clienteId), numero, fecha,
        items: items.filter((it) => it.inventarioPtId).map((it) => ({
          inventarioPtId: Number(it.inventarioPtId), cantidad: Number(it.cantidad), precio: Number(it.precio),
        })),
      });
      setModalAbierto(false);
      setClienteId(""); setNumero(""); setFecha(""); setItems([{ inventarioPtId: "", cantidad: "", precio: "" }]);
      recargar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo crear el pedido.");
    } finally {
      setEnviando(false);
    }
  };

  const facturar = async (p: Pedido) => {
    if (!window.confirm(`¿Facturar el pedido ${p.numero}? Esto descontará el inventario y abrirá la cartera por cobrar.`)) return;
    try {
      await api.post(`/comercial/pedidos/${p.id}/facturar`, {});
      recargar();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : "No se pudo facturar el pedido.");
    }
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        {puedeCrear && <button className="btn-primary" onClick={() => setModalAbierto(true)}>+ Nuevo pedido</button>}
      </div>
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-borderc"><th className="table-th">Número</th><th className="table-th">Cliente</th><th className="table-th">Fecha</th><th className="table-th">Total est.</th><th className="table-th">Estado</th><th className="table-th"></th></tr></thead>
          <tbody>
            {pedidos.map((p) => (
              <tr key={p.id} className="border-b border-borderc last:border-0">
                <td className="table-td font-mono text-xs font-semibold">{p.numero}</td>
                <td className="table-td">{p.clienteNombre}</td>
                <td className="table-td">{p.fecha}</td>
                <td className="table-td font-semibold">${p.totalEstimado.toLocaleString()}</td>
                <td className="table-td"><span className={`badge ${ESTADO_PEDIDO_BADGE[p.estado]}`}>{p.estado}</span></td>
                <td className="table-td text-right">
                  {puedeFacturar && p.estado === "confirmado" && (
                    <button className="text-accent-dark hover:underline text-xs font-semibold" onClick={() => facturar(p)}>Facturar</button>
                  )}
                </td>
              </tr>
            ))}
            {pedidos.length === 0 && <tr><td colSpan={6} className="table-td text-center text-textmuted py-8">Sin pedidos registrados.</td></tr>}
          </tbody>
        </table>
      </div>
      {modalAbierto && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4 py-8">
          <form onSubmit={crear} className="card w-full max-w-2xl p-6 space-y-4 max-h-[85vh] overflow-y-auto">
            <h3 className="text-base font-semibold text-slate-800">Nuevo pedido</h3>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="block text-xs font-semibold text-slate-600 mb-1">Cliente</label>
                <select className="input-field" value={clienteId} onChange={(e) => setClienteId(e.target.value)} required>
                  <option value="">Seleccione...</option>
                  {clientes.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select></div>
              <div><label className="block text-xs font-semibold text-slate-600 mb-1">Número</label>
                <input className="input-field font-mono" value={numero} onChange={(e) => setNumero(e.target.value)} placeholder="PED-0002" required /></div>
              <div><label className="block text-xs font-semibold text-slate-600 mb-1">Fecha</label>
                <input className="input-field" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} required /></div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-2">Ítems del pedido</label>
              <div className="space-y-2">
                {items.map((it, idx) => (
                  <div key={idx} className="grid grid-cols-6 gap-2">
                    <select className="input-field col-span-3" value={it.inventarioPtId} onChange={(e) => actualizarItem(idx, "inventarioPtId", e.target.value)}>
                      <option value="">Producto en inventario...</option>
                      {inventarioPt.map((inv) => (
                        <option key={inv.id} value={inv.id}>{inv.producto} · {inv.bodegaNombre} · disp. {inv.cantidad}</option>
                      ))}
                    </select>
                    <input className="input-field" type="number" step="0.01" placeholder="Cantidad" value={it.cantidad} onChange={(e) => actualizarItem(idx, "cantidad", e.target.value)} />
                    <input className="input-field" type="number" step="0.01" placeholder="Precio" value={it.precio} onChange={(e) => actualizarItem(idx, "precio", e.target.value)} />
                  </div>
                ))}
              </div>
              <button type="button" className="btn-ghost text-xs mt-2" onClick={() => setItems((prev) => [...prev, { inventarioPtId: "", cantidad: "", precio: "" }])}>+ Agregar ítem</button>
            </div>
            {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-ghost" onClick={() => setModalAbierto(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Guardando..." : "Crear pedido"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function TabFacturas({ facturas, clientes, inventarioPt, puedeCrear, puedeFacturar, puedeTimbrar, recargar }: {
  facturas: Factura[]; clientes: Cliente[]; inventarioPt: InvPT[]; puedeCrear: boolean; puedeFacturar: boolean; puedeTimbrar: boolean; recargar: () => void;
}) {
  const [timbrando, setTimbrando] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [modalManualAbierto, setModalManualAbierto] = useState(false);

  const timbrar = async (f: Factura) => {
    setError(null);
    setTimbrando(f.id);
    try {
      const resultado = await api.post(`/comercial/facturas/${f.id}/timbrar-dian`);
      if (resultado.estadoDian === "timbrada") {
        window.alert(`Factura ${f.numero} timbrada ante la DIAN.\nCUFE: ${resultado.cufeDian}`);
      } else {
        window.alert(`La DIAN respondió pero no aceptó la factura ${f.numero} (estado: ${resultado.estadoDian}). Revise respuestaDian en el detalle técnico.`);
      }
      recargar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo timbrar la factura ante la DIAN.");
    } finally {
      setTimbrando(null);
    }
  };

  const puedeFacturarManual = puedeCrear && puedeFacturar;

  const facturaAutomaticaDian = () => {
    window.alert(
      "Factura automática (DIAN) — próximamente.\n\n" +
      "Esta opción se conectará con la DIAN para revisar las facturas electrónicas ya causadas a nombre de la empresa " +
      "(como comprador) y registrarlas automáticamente. Por ahora, use \"Nueva factura\" para el ingreso manual."
    );
  };

  return (
    <div>
      <div className="flex justify-end gap-2 mb-3">
        {puedeFacturarManual && (
          <button className="btn-primary" onClick={() => setModalManualAbierto(true)}>+ Nueva factura</button>
        )}
        {puedeFacturarManual && (
          <button className="btn-ghost" onClick={facturaAutomaticaDian} title="Consultar y registrar automáticamente facturas causadas a nombre de la empresa ante la DIAN (próximamente)">
            Factura automática (DIAN)
          </button>
        )}
      </div>
    <div className="card overflow-hidden">
      {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 m-4">{error}</div>}
      <table className="w-full">
        <thead>
          <tr className="border-b border-borderc">
            <th className="table-th">Número</th><th className="table-th">Pedido</th><th className="table-th">Cliente</th>
            <th className="table-th">Fecha</th><th className="table-th">Estado DIAN</th><th className="table-th">CUFE</th>
            <th className="table-th">Total</th><th className="table-th"></th>
          </tr>
        </thead>
        <tbody>
          {facturas.map((f) => (
            <tr key={f.id} className="border-b border-borderc last:border-0">
              <td className="table-td font-mono text-xs font-semibold">{f.numero}</td>
              <td className="table-td font-mono text-xs">{f.pedidoNumero}</td>
              <td className="table-td">{f.clienteNombre}</td>
              <td className="table-td">{f.fecha}</td>
              <td className="table-td">
                <span className={`badge ${ESTADO_DIAN_BADGE[f.estadoDian] || "bg-slate-100 text-slate-500"}`}>{f.estadoDian}</span>
              </td>
              <td className="table-td font-mono text-[11px] text-textmuted" title={f.cufeDian || ""}>
                {f.cufeDian ? `${f.cufeDian.slice(0, 12)}…` : "—"}
              </td>
              <td className="table-td font-semibold">${f.valorTotal.toLocaleString()}</td>
              <td className="table-td text-right">
                {puedeTimbrar && f.estadoDian === "simulado" && (
                  <button
                    className="text-accent-dark hover:underline text-xs font-semibold disabled:opacity-50"
                    disabled={timbrando === f.id}
                    onClick={() => timbrar(f)}
                  >
                    {timbrando === f.id ? "Timbrando..." : "Timbrar con DIAN"}
                  </button>
                )}
              </td>
            </tr>
          ))}
          {facturas.length === 0 && <tr><td colSpan={8} className="table-td text-center text-textmuted py-8">Sin facturas emitidas.</td></tr>}
        </tbody>
      </table>
    </div>
      {modalManualAbierto && (
        <ModalNuevaFacturaManual
          clientes={clientes} inventarioPt={inventarioPt}
          onClose={() => setModalManualAbierto(false)}
          onCreada={() => { setModalManualAbierto(false); recargar(); }}
        />
      )}
    </div>
  );
}

// Ingreso manual de una factura de venta: por dentro crea un pedido ya
// confirmado y lo factura de inmediato (mismo motor que "Facturar" en la
// pestaña Pedidos — descuenta inventario, abre cartera y genera el
// comprobante contable), pero se presenta como un solo paso porque desde
// la perspectiva de Facturas no tiene sentido exponer el concepto interno
// de "pedido". El número de factura real (FV-xxxxx) lo asigna el backend
// al facturar; el número de pedido se genera internamente y no se muestra.
function ModalNuevaFacturaManual({ clientes, inventarioPt, onClose, onCreada }: {
  clientes: Cliente[]; inventarioPt: InvPT[]; onClose: () => void; onCreada: () => void;
}) {
  const [clienteId, setClienteId] = useState("");
  const [fecha, setFecha] = useState(() => new Date().toISOString().slice(0, 10));
  const [items, setItems] = useState<{ inventarioPtId: string; cantidad: string; precio: string }[]>([{ inventarioPtId: "", cantidad: "", precio: "" }]);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const actualizarItem = (idx: number, campo: string, valor: string) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [campo]: valor } : it)));
  };

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      const itemsValidos = items.filter((it) => it.inventarioPtId).map((it) => ({
        inventarioPtId: Number(it.inventarioPtId), cantidad: Number(it.cantidad), precio: Number(it.precio),
      }));
      if (itemsValidos.length === 0) throw new ApiError("Agregue al menos un ítem con producto, cantidad y precio.", 422, "validacion");
      const numeroPedidoInterno = `MAN-${Date.now()}`;
      const pedido = await api.post("/comercial/pedidos", { clienteId: Number(clienteId), numero: numeroPedidoInterno, fecha, items: itemsValidos });
      await api.post(`/comercial/pedidos/${pedido.id}/facturar`, {});
      onCreada();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo crear la factura.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4 py-8">
      <form onSubmit={crear} className="card w-full max-w-2xl p-6 space-y-4 max-h-[85vh] overflow-y-auto">
        <h3 className="text-base font-semibold text-slate-800">Nueva factura (ingreso manual)</h3>
        <p className="text-xs text-textmuted -mt-2">Descuenta el inventario de producto terminado y abre la cartera por cobrar de inmediato, igual que facturar un pedido.</p>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Cliente</label>
            <select className="input-field" value={clienteId} onChange={(e) => setClienteId(e.target.value)} required>
              <option value="">Seleccione...</option>
              {clientes.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select></div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-1">Fecha</label>
            <input className="input-field" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} required /></div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-2">Ítems de la factura</label>
          <div className="space-y-2">
            {items.map((it, idx) => (
              <div key={idx} className="grid grid-cols-6 gap-2">
                <select className="input-field col-span-3" value={it.inventarioPtId} onChange={(e) => actualizarItem(idx, "inventarioPtId", e.target.value)}>
                  <option value="">Producto en inventario...</option>
                  {inventarioPt.map((inv) => (
                    <option key={inv.id} value={inv.id}>{inv.producto} · {inv.bodegaNombre} · disp. {inv.cantidad}</option>
                  ))}
                </select>
                <input className="input-field" type="number" step="0.01" placeholder="Cantidad" value={it.cantidad} onChange={(e) => actualizarItem(idx, "cantidad", e.target.value)} />
                <input className="input-field" type="number" step="0.01" placeholder="Precio" value={it.precio} onChange={(e) => actualizarItem(idx, "precio", e.target.value)} />
              </div>
            ))}
          </div>
          <button type="button" className="btn-ghost text-xs mt-2" onClick={() => setItems((prev) => [...prev, { inventarioPtId: "", cantidad: "", precio: "" }])}>+ Agregar ítem</button>
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Facturando..." : "Crear factura"}</button>
        </div>
      </form>
    </div>
  );
}

const AGING_BUCKETS: { key: string; label: string }[] = [
  { key: "porVencer", label: "Por vencer" },
  { key: "dias_0_30", label: "0-30 días" },
  { key: "dias_31_60", label: "31-60 días" },
  { key: "dias_61_90", label: "61-90 días" },
  { key: "masDe90", label: "Más de 90 días" },
];

function TabCartera({ cartera, puedeFacturar, recargar }: { cartera: CarteraItem[]; puedeFacturar: boolean; recargar: () => void }) {
  const [aging, setAging] = useState<any | null>(null);

  useEffect(() => { api.get("/comercial/cartera-aging").then(setAging).catch(() => setAging(null)); }, [cartera]);

  const marcarPagada = async (c: CarteraItem) => {
    try { await api.put(`/comercial/cartera/${c.id}/pagar`, {}); recargar(); } catch { /* noop */ }
  };
  return (
    <div>
      {aging && (
        <div className="card p-4 mb-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Antigüedad de saldos · corte {aging.corte}</p>
          <div className="grid grid-cols-5 gap-3">
            {AGING_BUCKETS.map((b) => (
              <div key={b.key} className={`rounded-md px-3 py-2 ${b.key === "masDe90" && aging.resumen[b.key] > 0 ? "bg-red-50" : "bg-surface"}`}>
                <div className="text-[10px] text-textmuted uppercase">{b.label}</div>
                <div className={`text-sm font-semibold ${b.key === "masDe90" && aging.resumen[b.key] > 0 ? "text-red-700" : "text-slate-800"}`}>${aging.resumen[b.key].toLocaleString()}</div>
              </div>
            ))}
          </div>
          <div className="flex justify-between text-sm font-semibold text-accent-dark border-t border-borderc pt-2 mt-3">
            <span>Total cartera pendiente</span><span>${aging.total.toLocaleString()}</span>
          </div>
        </div>
      )}
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-borderc"><th className="table-th">Factura</th><th className="table-th">Cliente</th><th className="table-th">Saldo</th><th className="table-th">Vence</th><th className="table-th">Estado</th><th className="table-th"></th></tr></thead>
          <tbody>
            {cartera.map((c) => (
              <tr key={c.id} className="border-b border-borderc last:border-0">
                <td className="table-td font-mono text-xs font-semibold">{c.facturaNumero}</td>
                <td className="table-td">{c.clienteNombre}</td>
                <td className="table-td font-semibold">${c.saldo.toLocaleString()}</td>
                <td className="table-td">{c.fechaVencimiento}</td>
                <td className="table-td"><span className={`badge ${c.estado === "pagada" ? "bg-accent-light text-accent-dark" : "bg-amber-50 text-amber-700"}`}>{c.estado}</span></td>
                <td className="table-td text-right">
                  {puedeFacturar && c.estado !== "pagada" && (
                    <button className="text-accent-dark hover:underline text-xs font-semibold" onClick={() => marcarPagada(c)}>Marcar pagada</button>
                  )}
                </td>
              </tr>
            ))}
            {cartera.length === 0 && <tr><td colSpan={6} className="table-td text-center text-textmuted py-8">Sin cuentas por cobrar.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TabCotizaciones({ cotizaciones, clientes, inventarioPt, puedeCrear, puedeEditar, recargar }: {
  cotizaciones: Cotizacion[]; clientes: Cliente[]; inventarioPt: InvPT[]; puedeCrear: boolean; puedeEditar: boolean; recargar: () => void;
}) {
  const [modalAbierto, setModalAbierto] = useState(false);
  const [clienteId, setClienteId] = useState("");
  const [numero, setNumero] = useState("");
  const [fecha, setFecha] = useState("");
  const [fechaVencimiento, setFechaVencimiento] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [items, setItems] = useState<{ producto: string; cantidad: string; precio: string }[]>([{ producto: "", cantidad: "", precio: "" }]);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [convirtiendo, setConvirtiendo] = useState<Cotizacion | null>(null);

  const actualizarItem = (idx: number, campo: string, valor: string) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [campo]: valor } : it)));
  };

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.post("/comercial/cotizaciones", {
        clienteId: Number(clienteId), numero, fecha, fechaVencimiento: fechaVencimiento || null, observaciones: observaciones || null,
        items: items.filter((it) => it.producto).map((it) => ({ producto: it.producto, cantidad: Number(it.cantidad), precio: Number(it.precio) })),
      });
      setModalAbierto(false);
      setClienteId(""); setNumero(""); setFecha(""); setFechaVencimiento(""); setObservaciones("");
      setItems([{ producto: "", cantidad: "", precio: "" }]);
      recargar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo crear la cotización.");
    } finally {
      setEnviando(false);
    }
  };

  const cambiarEstado = async (c: Cotizacion, estado: string) => {
    try { await api.put(`/comercial/cotizaciones/${c.id}`, { estado }); recargar(); } catch (e) { alert(e instanceof ApiError ? e.message : "No se pudo actualizar la cotización."); }
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        {puedeCrear && <button className="btn-primary" onClick={() => setModalAbierto(true)}>+ Nueva cotización</button>}
      </div>
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-borderc"><th className="table-th">Número</th><th className="table-th">Cliente</th><th className="table-th">Fecha</th><th className="table-th">Vence</th><th className="table-th">Total</th><th className="table-th">Estado</th><th className="table-th"></th></tr></thead>
          <tbody>
            {cotizaciones.map((c) => {
              const total = c.detalle.reduce((s, it) => s + it.cantidad * it.precio, 0);
              return (
                <tr key={c.id} className="border-b border-borderc last:border-0">
                  <td className="table-td font-mono text-xs font-semibold">{c.numero}</td>
                  <td className="table-td">{c.clienteNombre}</td>
                  <td className="table-td">{c.fecha}</td>
                  <td className="table-td">{c.fechaVencimiento || "—"}</td>
                  <td className="table-td font-semibold">${total.toLocaleString()}</td>
                  <td className="table-td"><span className={`badge ${ESTADO_COTIZACION_BADGE[c.estado]}`}>{c.estado}</span></td>
                  <td className="table-td text-right whitespace-nowrap">
                    {puedeEditar && c.estado === "borrador" && (
                      <button className="text-accent-dark hover:underline text-xs font-semibold mr-3" onClick={() => cambiarEstado(c, "enviada")}>Enviar</button>
                    )}
                    {puedeEditar && c.estado === "enviada" && (
                      <>
                        <button className="text-accent-dark hover:underline text-xs font-semibold mr-3" onClick={() => cambiarEstado(c, "aceptada")}>Aceptar</button>
                        <button className="text-red-700 hover:underline text-xs font-semibold mr-3" onClick={() => cambiarEstado(c, "rechazada")}>Rechazar</button>
                      </>
                    )}
                    {puedeCrear && c.estado === "aceptada" && (
                      <button className="text-accent-dark hover:underline text-xs font-semibold" onClick={() => setConvirtiendo(c)}>Convertir a pedido</button>
                    )}
                    {c.estado === "convertida" && <span className="text-xs text-textmuted">Pedido #{c.pedidoId}</span>}
                  </td>
                </tr>
              );
            })}
            {cotizaciones.length === 0 && <tr><td colSpan={7} className="table-td text-center text-textmuted py-8">Sin cotizaciones registradas.</td></tr>}
          </tbody>
        </table>
      </div>

      {modalAbierto && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4 py-8">
          <form onSubmit={crear} className="card w-full max-w-2xl p-6 space-y-4 max-h-[85vh] overflow-y-auto">
            <h3 className="text-base font-semibold text-slate-800">Nueva cotización</h3>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-xs font-semibold text-slate-600 mb-1">Cliente</label>
                <select className="input-field" value={clienteId} onChange={(e) => setClienteId(e.target.value)} required>
                  <option value="">Seleccione...</option>
                  {clientes.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select></div>
              <div><label className="block text-xs font-semibold text-slate-600 mb-1">Número</label>
                <input className="input-field font-mono" value={numero} onChange={(e) => setNumero(e.target.value)} placeholder="COT-0001" required /></div>
              <div><label className="block text-xs font-semibold text-slate-600 mb-1">Fecha</label>
                <input className="input-field" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} required /></div>
              <div><label className="block text-xs font-semibold text-slate-600 mb-1">Vence (opcional)</label>
                <input className="input-field" type="date" value={fechaVencimiento} onChange={(e) => setFechaVencimiento(e.target.value)} /></div>
              <div className="col-span-2"><label className="block text-xs font-semibold text-slate-600 mb-1">Observaciones</label>
                <input className="input-field" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} /></div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-2">Ítems cotizados</label>
              <p className="text-[11px] text-textmuted mb-2">La cotización no necesita existencia real — se describe libremente. La validación de inventario ocurre al convertirla a pedido.</p>
              <div className="space-y-2">
                {items.map((it, idx) => (
                  <div key={idx} className="grid grid-cols-6 gap-2">
                    <input className="input-field col-span-3" placeholder="Producto" value={it.producto} onChange={(e) => actualizarItem(idx, "producto", e.target.value)} />
                    <input className="input-field" type="number" step="0.01" placeholder="Cantidad" value={it.cantidad} onChange={(e) => actualizarItem(idx, "cantidad", e.target.value)} />
                    <input className="input-field" type="number" step="0.01" placeholder="Precio" value={it.precio} onChange={(e) => actualizarItem(idx, "precio", e.target.value)} />
                  </div>
                ))}
              </div>
              <button type="button" className="btn-ghost text-xs mt-2" onClick={() => setItems((prev) => [...prev, { producto: "", cantidad: "", precio: "" }])}>+ Agregar ítem</button>
            </div>
            {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-ghost" onClick={() => setModalAbierto(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Guardando..." : "Crear cotización"}</button>
            </div>
          </form>
        </div>
      )}

      {convirtiendo && (
        <ModalConvertirCotizacion cotizacion={convirtiendo} inventarioPt={inventarioPt}
          onClose={() => setConvirtiendo(null)} onConvertida={() => { setConvirtiendo(null); recargar(); }} />
      )}
    </div>
  );
}

function ModalConvertirCotizacion({ cotizacion, inventarioPt, onClose, onConvertida }: {
  cotizacion: Cotizacion; inventarioPt: InvPT[]; onClose: () => void; onConvertida: () => void;
}) {
  const [numeroPedido, setNumeroPedido] = useState("");
  const [seleccion, setSeleccion] = useState<Record<number, string>>({});
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const convertir = async (e: React.FormEvent) => {
    e.preventDefault();
    const items = cotizacion.detalle
      .filter((it) => seleccion[it.id])
      .map((it) => ({ inventarioPtId: Number(seleccion[it.id]), cantidad: it.cantidad, precio: it.precio }));
    if (items.length === 0) {
      setError("Seleccione la existencia de inventario para cada ítem.");
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      await api.post(`/comercial/cotizaciones/${cotizacion.id}/convertir`, { numeroPedido, items });
      onConvertida();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo convertir la cotización.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4 py-8">
      <form onSubmit={convertir} className="card w-full max-w-2xl p-6 space-y-4 max-h-[85vh] overflow-y-auto">
        <h3 className="text-base font-semibold text-slate-800">Convertir {cotizacion.numero} a pedido</h3>
        <p className="text-[11px] text-textmuted">
          Cada línea cotizada debe mapearse a una existencia real de producto terminado, con la misma validación que un pedido nuevo.
        </p>
        <div><label className="block text-xs font-semibold text-slate-600 mb-1">Número de pedido</label>
          <input className="input-field font-mono" value={numeroPedido} onChange={(e) => setNumeroPedido(e.target.value)} placeholder="PED-0003" required /></div>
        <div className="space-y-2">
          {cotizacion.detalle.map((it) => (
            <div key={it.id} className="grid grid-cols-6 gap-2 items-center">
              <div className="col-span-2 text-xs text-slate-700">{it.producto}<br /><span className="text-textmuted">cant. {it.cantidad} · ${it.precio.toLocaleString()}</span></div>
              <select className="input-field col-span-4" value={seleccion[it.id] || ""} onChange={(e) => setSeleccion((prev) => ({ ...prev, [it.id]: e.target.value }))} required>
                <option value="">Existencia en inventario...</option>
                {inventarioPt.map((inv) => (
                  <option key={inv.id} value={inv.id}>{inv.producto} · {inv.bodegaNombre} · disp. {inv.cantidad}</option>
                ))}
              </select>
            </div>
          ))}
        </div>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Convirtiendo..." : "Convertir a pedido"}</button>
        </div>
      </form>
    </div>
  );
}

function TabSeguimientos({ seguimientos, clientes, puedeCrear, recargar }: {
  seguimientos: Seguimiento[]; clientes: Cliente[]; puedeCrear: boolean; recargar: () => void;
}) {
  const [modalAbierto, setModalAbierto] = useState(false);
  const [clienteId, setClienteId] = useState("");
  const [tipo, setTipo] = useState("llamada");
  const [fecha, setFecha] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [proximoSeguimiento, setProximoSeguimiento] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.post("/comercial/seguimientos", {
        clienteId: Number(clienteId), tipo, fecha, descripcion, proximoSeguimiento: proximoSeguimiento || null,
      });
      setModalAbierto(false);
      setClienteId(""); setTipo("llamada"); setFecha(""); setDescripcion(""); setProximoSeguimiento("");
      recargar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo registrar el seguimiento.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div>
      <p className="text-xs text-textmuted mb-3">
        CRM ligero: cada gestión comercial con un cliente (llamada, visita, correo) queda registrada aquí, con una fecha de
        próximo seguimiento opcional para armar un pendiente de gestión.
      </p>
      <div className="flex justify-end mb-3">
        {puedeCrear && <button className="btn-primary" onClick={() => setModalAbierto(true)}>+ Nuevo seguimiento</button>}
      </div>
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-borderc"><th className="table-th">Cliente</th><th className="table-th">Tipo</th><th className="table-th">Fecha</th><th className="table-th">Descripción</th><th className="table-th">Próximo</th><th className="table-th">Usuario</th></tr></thead>
          <tbody>
            {seguimientos.map((s) => (
              <tr key={s.id} className="border-b border-borderc last:border-0">
                <td className="table-td font-medium">{s.clienteNombre}</td>
                <td className="table-td"><span className="badge bg-slate-100 text-slate-500">{TIPO_SEGUIMIENTO_LABEL[s.tipo] || s.tipo}</span></td>
                <td className="table-td">{s.fecha}</td>
                <td className="table-td max-w-xs truncate" title={s.descripcion}>{s.descripcion}</td>
                <td className="table-td">{s.proximoSeguimiento || "—"}</td>
                <td className="table-td text-xs text-textmuted">{s.usuarioNombre || "—"}</td>
              </tr>
            ))}
            {seguimientos.length === 0 && <tr><td colSpan={6} className="table-td text-center text-textmuted py-8">Sin seguimientos registrados.</td></tr>}
          </tbody>
        </table>
      </div>
      {modalAbierto && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
            <h3 className="text-base font-semibold text-slate-800">Nuevo seguimiento comercial</h3>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Cliente</label>
              <select className="input-field" value={clienteId} onChange={(e) => setClienteId(e.target.value)} required>
                <option value="">Seleccione...</option>
                {clientes.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Tipo</label>
              <select className="input-field" value={tipo} onChange={(e) => setTipo(e.target.value)}>
                {Object.entries(TIPO_SEGUIMIENTO_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Fecha</label>
              <input className="input-field" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} required /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Descripción</label>
              <textarea className="input-field" rows={3} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} required /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Próximo seguimiento (opcional)</label>
              <input className="input-field" type="date" value={proximoSeguimiento} onChange={(e) => setProximoSeguimiento(e.target.value)} /></div>
            {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-ghost" onClick={() => setModalAbierto(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={enviando}>{enviando ? "Guardando..." : "Registrar"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
